import { Link, useParams } from 'react-router-dom';
import { useSEO, SEOInjection } from '@/hooks/useSEO';
import { categoryArticles, allCategories } from '@/mocks/categoryData';
import { newsImageUrls, categoryHeroBg } from '@/mocks/imageUrls';
import { ArticleCard } from '@/components/base/Card';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryArticles[slug] : null;

  if (!category) {
    return <NotFound />;
  }

  const canonicalUrl = `${window.location.origin}/category/${category.slug}`;

  return (
    <>
      <CategorySEO category={category} canonicalUrl={canonicalUrl} />

      <main>
        <section className="relative pt-20 md:pt-24 pb-12 md:pb-16 bg-foreground-900 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={categoryHeroBg}
              alt={category.name}
              className="w-full h-full object-cover object-top opacity-30"
              title={category.name}
            />
          </div>
          <div className="relative z-10 container-custom">
            <div className="max-w-3xl">
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-4">
                {category.name}
              </h1>
              <p className="text-base md:text-lg text-background-50/70 leading-relaxed">
                {category.heroDesc}
              </p>
            </div>
          </div>
        </section>

        <section className="bg-background-50 border-b border-background-200/60">
          <div className="container-custom py-3 overflow-x-auto">
            <div className="flex items-center space-x-1 min-w-max">
              {allCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all duration-200 cursor-pointer
                    ${cat.slug === category.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }
                  `}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-background-50">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.articles.map((article) => (
                <ArticleCard
                  key={article.slug}
                  slug={article.slug}
                  title={article.title}
                  excerpt={article.excerpt}
                  date={article.date}
                  category={category.name}
                  categorySlug={category.slug}
                  imageUrl={newsImageUrls[article.imageSeq] || newsImageUrls['news-summit-2026']}
                />
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

function CategorySEO({ category, canonicalUrl }: { category: { slug: string; name: string; description: string }; canonicalUrl: string }) {
  useSEO({
    title: `${category.name} - 豪门国际 | 豪门国际官网`,
    description: `豪门国际${category.name}板块：${category.description}。汇聚全球顶级资源，为您提供专业的${category.name}服务与资讯。`,
    keywords: `豪门国际,${category.name},豪门资源,国际资源对接,高端商务圈,精英社交`,
    canonical: canonicalUrl,
    ogTitle: `${category.name} - 豪门国际`,
    ogDescription: category.description,
    ogUrl: canonicalUrl,
  });

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 豪门国际`,
    description: category.description,
    url: canonicalUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: '豪门国际',
      url: window.location.origin,
    },
  };

  return <SEOInjection jsonLd={collectionSchema} />;
}